import { useNavigation, useRoute } from 'app/hooks/navigation';
import { SideMenuManagment } from 'app/screens/management/components/side-menu-managment';

import React from 'react';
import { ScrollView } from 'react-native-gesture-handler';
import { SideMenuProfile } from '../profile/side-menu-profile';
import { CHAT_WEB_COLUMN_HEIGHT_RATIO } from './dashboard-layout-constants';

import { AllChannelsScreenComponent } from 'app/screens/chat/all-channels/components/all-channels';
import { useWindowDimensions } from 'react-native';
import { createTheme as createDSTheme } from 'app/shopify';
const theme = createDSTheme();

type RouteComponentMap = Record<
  string,
  { component: React.ComponentType; width: number; props?: Record<string, unknown> }
>;

const DEFAULT_WIDTH = 350;
const CHAT_SIDE_MENU_WIDTH = 450;

const PROFILE_SIDE_MENU = { component: SideMenuProfile, width: DEFAULT_WIDTH };
const MANAGEMENT_SIDE_MENU = {
  component: SideMenuManagment,
  width: DEFAULT_WIDTH + theme.spacing['spacing.general.x12'],
};
const CHAT_SIDE_MENU = {
  component: AllChannelsScreenComponent,
  width: CHAT_SIDE_MENU_WIDTH,
};

const SideMenuComponentPerPage: RouteComponentMap = {
  'account-deletion': PROFILE_SIDE_MENU,
  'contact-us': PROFILE_SIDE_MENU,
  language: PROFILE_SIDE_MENU,
  'my-address': PROFILE_SIDE_MENU,
  payment: PROFILE_SIDE_MENU,
  'change-password': PROFILE_SIDE_MENU,
  'my-plan': PROFILE_SIDE_MENU,
  'my-preferences': PROFILE_SIDE_MENU,
  'my-ratings': PROFILE_SIDE_MENU,
  'super-partageur': PROFILE_SIDE_MENU,
  'super-partageur-details': PROFILE_SIDE_MENU,
  'my-impact-financial': PROFILE_SIDE_MENU,
  'profile-overview': PROFILE_SIDE_MENU,
  privacy: PROFILE_SIDE_MENU,
  'notifications-setting': PROFILE_SIDE_MENU,
  'calendar-setting': PROFILE_SIDE_MENU,
  'group-members': PROFILE_SIDE_MENU,
  'my-clubs': PROFILE_SIDE_MENU,
  'my-interests': PROFILE_SIDE_MENU,
  'my-neighbourhood': PROFILE_SIDE_MENU,
  ambassador: PROFILE_SIDE_MENU,
  streak: PROFILE_SIDE_MENU,

  // Shared
  'management-overview': MANAGEMENT_SIDE_MENU,
  'my-loans': MANAGEMENT_SIDE_MENU,
  'my-listings': MANAGEMENT_SIDE_MENU,
  favourites: MANAGEMENT_SIDE_MENU,
  'past-loans': MANAGEMENT_SIDE_MENU,

  // Chat
  'all-channels': CHAT_SIDE_MENU,
  'one-channel': CHAT_SIDE_MENU,
} as const;

function SideMenu() {
  const currentRoute = useRoute();
  const navigation = useNavigation();
  const windowHeight = useWindowDimensions().height;

  const isOneOfChatPages = ['all-channels', 'one-channel'].includes(currentRoute.name);
  const { routeExistInStack, SideMenuComponent, width, props } = (() => {
    const stackRoutesName = navigation.getState().routeNames;
    const routeExists = stackRoutesName.includes(currentRoute.name);
    const component = routeExists ? SideMenuComponentPerPage[currentRoute.name]?.component : null;
    const componentWidth = routeExists ? SideMenuComponentPerPage[currentRoute.name]?.width : 0;

    return {
      routeExistInStack: routeExists,
      SideMenuComponent: component,
      width: componentWidth,
      props: SideMenuComponentPerPage[currentRoute.name]?.props,
    };
  })();

  if (!routeExistInStack || !SideMenuComponent || !width) {
    return null;
  }

  const horizontalPad = theme.spacing['spacing.general.x4'];
  const innerWidth = width - 2 * horizontalPad;

  const chatColumnHeight = CHAT_WEB_COLUMN_HEIGHT_RATIO * windowHeight;
  const contentContainerStyle = {
    width: innerWidth,
    paddingHorizontal: 0,
    paddingTop: !isOneOfChatPages ? theme.spacing['spacing.general.x6'] : 0,
    paddingBottom: isOneOfChatPages ? 0 : theme.spacing['spacing.general.x12'] * 2,
    ...(isOneOfChatPages ? { height: chatColumnHeight } : { flexGrow: 1 }),
  };

  return (
    <ScrollView
      style={{
        width,
        flexGrow: 0,
        flexShrink: 0,
        ...(isOneOfChatPages
          ? { height: chatColumnHeight, minHeight: chatColumnHeight, maxHeight: chatColumnHeight }
          : { minHeight: 0, maxHeight: windowHeight }),
        borderRightWidth: !isOneOfChatPages ? 1 : 0,
        borderColor: theme.colors['surface.inverse.tertiary-neutral'],
        paddingLeft: horizontalPad,
        paddingRight: horizontalPad,
      }}
      contentContainerStyle={contentContainerStyle}
      showsVerticalScrollIndicator
    >
      {isOneOfChatPages ? (
        <AllChannelsScreenComponent fixedContainerWidth={innerWidth} />
      ) : (
        <SideMenuComponent {...props} />
      )}
    </ScrollView>
  );
}

export default SideMenu;
